<template>
    <div class="min-h-screen bg-[#F4F5F7] font-sans text-[#111]">

        <!-- Mobile topbar -->
        <header :class="[t.mobileTopbar, 'fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4 md:hidden']">
            <button :class="[t.mobileMenuBtn, 'p-2 rounded-md transition-colors']" @click="sidebarOpen = true">
                <i class="fas fa-bars text-base"></i>
            </button>
            <div class="flex items-center gap-2 cursor-pointer" onclick="window.location.href='/app'">
                <img :src="t.logoUrl" class="h-7" alt="TermoFlow">
                <span :class="[t.logoText, 'font-black text-base tracking-tight']">TermoFlow</span>
            </div>
            <div class="w-9"></div>
        </header>

        <!-- Desktop topbar -->
        <header class="fixed top-0 left-[280px] right-0 z-40 h-14 bg-white border-b border-gray-200 hidden md:flex items-center justify-between px-6">
            <div class="flex items-center relative group w-[420px]">
                <i class="fas fa-search absolute left-3.5 text-gray-400 text-xs z-10"></i>
                <input
                    v-model="searchQuery"
                    @input="onSearchInput"
                    @focus="searchOpen = true"
                    type="text"
                    placeholder="Rechercher un client, une intervention..."
                    class="w-full pl-9 pr-4 py-2 bg-gray-100 border border-transparent rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-gray-300 transition-all duration-200"
                />
                <div v-if="searchOpen && (searchLoading || hasResults)"
                    class="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                    <div v-if="searchLoading" class="p-4 text-sm text-gray-500 flex items-center gap-2">
                        <i class="fas fa-circle-notch fa-spin text-orange-500 text-xs"></i> Recherche…
                    </div>
                    <div v-else class="py-1.5 max-h-80 overflow-y-auto">
                        <div v-if="searchResults.clients.length">
                            <div class="px-4 pt-2.5 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Clients</div>
                            <a v-for="client in searchResults.clients" :key="'client-' + client.id"
                                @click.prevent="goToClient(client)" href="#"
                                class="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors group">
                                <div>
                                    <div class="text-sm font-medium text-gray-900">{{ client.nom }} {{ client.prenom }}</div>
                                    <div class="text-xs text-gray-400">{{ client.email }}</div>
                                </div>
                                <i class="fas fa-chevron-right text-[10px] text-gray-300 group-hover:text-orange-500 transition-colors"></i>
                            </a>
                        </div>
                        <div v-if="searchResults.interventions.length">
                            <div class="px-4 pt-2.5 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-widest border-t border-gray-100 mt-1">Interventions</div>
                            <a v-for="intervention in searchResults.interventions" :key="'intervention-' + intervention.id"
                                @click.prevent="goToIntervention(intervention)" href="#"
                                class="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors group">
                                <div class="flex-1 min-w-0">
                                    <div class="text-sm font-medium text-gray-900">{{ intervention.reference || '#' + intervention.id }}</div>
                                    <div class="text-xs text-gray-400 truncate">{{ intervention.description }}</div>
                                </div>
                                <div class="text-xs text-gray-400 ml-3 shrink-0">{{ intervention.date_intervention | momentDate }}</div>
                            </a>
                        </div>
                        <div v-if="!searchResults.clients.length && !searchResults.interventions.length" class="px-4 py-6 text-center text-sm text-gray-400">Aucun résultat</div>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-3">
                <button v-if="warningLicence" @click="$router.push({name: 'LicenceExpiration'})"
                    class="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-orange-100 transition-colors">
                    <i class="fas fa-exclamation-triangle text-xs"></i> {{ warningLicence }}
                </button>
            </div>
        </header>

        <div v-if="sidebarOpen" class="fixed inset-0 bg-black/50 z-40 md:hidden" @click="sidebarOpen = false"></div>

        <aside :class="[t.sidebar, 'theme-' + theme, 'fixed top-0 left-0 z-50 h-screen w-[280px] transform transition-transform duration-300 md:translate-x-0 flex flex-col', sidebarOpen ? 'translate-x-0' : '-translate-x-full']">

            <!-- Logo -->
            <div :class="[t.logoBorder, 'flex items-center gap-2.5 h-14 px-5 shrink-0']">
                <img :src="t.logoUrl" class="h-7" alt="TermoFlow">
                <span :class="[t.logoText, 'text-[17px] font-black tracking-tight']">TermoFlow</span>
            </div>

            <!-- CTA + mobile banner -->
            <div class="px-4 pt-4 pb-3 shrink-0 space-y-2.5">
                <template v-if="$account.account.can_intervention">
                    <button @click="$modal.show('modal-add-intervention-template')"
                        :class="[t.ctaBtn, 'w-full px-4 py-2.5 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2']">
                        <i class="fas fa-plus text-xs"></i>
                        Nouvelle intervention
                    </button>
                </template>
                <template v-if="!$account.account.mobile_token">
                    <div :class="[t.mobileBanner, 'rounded-md p-3 flex items-center gap-3']">
                        <div :class="[t.mobileBannerIconBg, 'w-7 h-7 rounded flex items-center justify-center shrink-0']">
                            <i :class="[t.mobileBannerIcon, 'fas fa-mobile-alt text-xs']"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p :class="[t.mobileBannerTitle, 'text-xs font-semibold leading-tight']">Application mobile</p>
                            <p :class="[t.mobileBannerSub, 'text-[11px] mt-0.5']">Non connectée</p>
                        </div>
                        <button @click="$modal.show('modal-connect-mobile')"
                            :class="[t.mobileBannerBtn, 'text-[11px] font-semibold transition-colors shrink-0 whitespace-nowrap']">
                            Activer →
                        </button>
                    </div>
                </template>
            </div>

            <!-- Navigation -->
            <nav class="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">

                <p :class="[t.sectionLabel, 'px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.12em]']">Principal</p>

                <a href="/app" class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'home' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-home w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'home' ? t.iconActive : t.icon"></i>
                    <span>Accueil</span>
                </a>

                <a href="/app/clients" class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'clients' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-users w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'clients' ? t.iconActive : t.icon"></i>
                    <span>Clients</span>
                </a>

                <a href="/app/planning" class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'planning' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-calendar-alt w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'planning' ? t.iconActive : t.icon"></i>
                    <span>Planning</span>
                </a>

                <a href="/app/chantiers" class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'chantiers' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-helmet-safety w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'chantiers' ? t.iconActive : t.icon"></i>
                    <span>Chantiers</span>
                    <span v-if="pastilles.chantiers_termines" :class="[t.badge, 'ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none']">{{ pastilles.chantiers_termines }}</span>
                </a>

                <a href="/app/interventions" class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'interventions' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-clipboard-check w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'interventions' ? t.iconActive : t.icon"></i>
                    <span>Interventions</span>
                    <span v-if="pastilles.interventions_a_planifier" :class="[t.badge, 'ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none']">{{ pastilles.interventions_a_planifier }}</span>
                </a>

                <a v-if="$account.account.can_invoice" href="/app/facturations"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2"
                    :class="currentRouteKey == 'facturations' ? t.navItemActive : t.navItemInactive">
                    <i class="fas fa-file-invoice-dollar w-4 text-center text-xs shrink-0" :class="currentRouteKey == 'facturations' ? t.iconActive : t.icon"></i>
                    <span>Facturation</span>
                    <span v-if="pastilles.facturation" :class="[t.badge, 'ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none']">{{ pastilles.facturation }}</span>
                </a>

                <p :class="[t.sectionLabel, 'px-3 pb-1.5 pt-5 text-[10px] font-semibold uppercase tracking-[0.12em]']">Gestion entreprise</p>

                <template v-if="$account.account.can_stock">
                    <button @click="materielOpen = !materielOpen"
                        :class="[t.navItemInactive, 'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2']">
                        <i :class="[t.icon, 'fas fa-toolbox w-4 text-center text-xs shrink-0']"></i>
                        <span>Matériel</span>
                        <i :class="[t.chevron, 'fas fa-chevron-down text-[10px] ml-auto transition-transform duration-200 shrink-0', materielOpen ? 'rotate-180' : '']"></i>
                    </button>
                    <div :class="[t.submenuBorder, 'overflow-hidden transition-all duration-200 ml-7 pl-3 border-l']"
                        :style="materielOpen ? 'max-height: 200px; opacity: 1' : 'max-height: 0; opacity: 0'">
                        <a href="/app/stock" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'stock' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-gears w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'stock' ? t.iconActive : t.subIcon"></i>Stock pièces
                        </a>
                        <a href="/app/bouteilles" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'bouteilles' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-bottle-water w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'bouteilles' ? t.iconActive : t.subIcon"></i>Bouteilles de gaz
                            <span v-if="pastilles.bouteilles" :class="[t.badge, 'ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none']">{{ pastilles.bouteilles }}</span>
                        </a>
                        <a href="/app/detecteurs" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'detecteurs' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-mobile-retro w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'detecteurs' ? t.iconActive : t.subIcon"></i>Détecteurs de gaz
                            <span v-if="pastilles.detecteurs" :class="[t.badge, 'ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded leading-none']">{{ pastilles.detecteurs }}</span>
                        </a>
                    </div>
                </template>

                <button @click="documentsOpen = !documentsOpen"
                    :class="[t.navItemInactive, 'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2']">
                    <i :class="[t.icon, 'fas fa-folder-open w-4 text-center text-xs shrink-0']"></i>
                    <span>Documents</span>
                    <i :class="[t.chevron, 'fas fa-chevron-down text-[10px] ml-auto transition-transform duration-200 shrink-0', documentsOpen ? 'rotate-180' : '']"></i>
                </button>
                <div :class="[t.submenuBorder, 'overflow-hidden transition-all duration-200 ml-7 pl-3 border-l']"
                    :style="documentsOpen ? 'max-height: 200px; opacity: 1' : 'max-height: 0; opacity: 0'">
                    <a href="/app/contrats" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'contrats' ? t.subItemActive : t.subItemInactive">
                        <i class="fas fa-file-contract w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'contrats' ? t.iconActive : t.subIcon"></i>Contrats
                    </a>
                    <a href="/app/attestation-entretiens" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'attestation_entretien' ? t.subItemActive : t.subItemInactive">
                        <i class="fas fa-file-circle-check w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'attestation_entretien' ? t.iconActive : t.subIcon"></i>Attestations d'entretien
                    </a>
                    <a href="/app/cerfa" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'cerfa' ? t.subItemActive : t.subItemInactive">
                        <i class="fas fa-file w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'cerfa' ? t.iconActive : t.subIcon"></i>Cerfa
                    </a>
                </div>

                <template v-if="$account.account.can_stats">
                    <button @click="gestionOpen = !gestionOpen"
                        :class="[t.navItemInactive, 'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2']">
                        <i :class="[t.icon, 'fas fa-chart-bar w-4 text-center text-xs shrink-0']"></i>
                        <span>Supervision</span>
                        <i :class="[t.chevron, 'fas fa-chevron-down text-[10px] ml-auto transition-transform duration-200 shrink-0', gestionOpen ? 'rotate-180' : '']"></i>
                    </button>
                    <div :class="[t.submenuBorder, 'overflow-hidden transition-all duration-200 ml-7 pl-3 border-l']"
                        :style="gestionOpen ? 'max-height: 200px; opacity: 1' : 'max-height: 0; opacity: 0'">
                        <a v-if="!$account.account.societe.solo"
                            :href="$account.account.societe.independant == 1 ? '/app/statistiques' : '/app/statistiques-premium'"
                            class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150"
                            :class="currentRouteKey == 'statistiques' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-chart-line w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'statistiques' ? t.iconActive : t.subIcon"></i>Statistiques
                        </a>
                        <a href="/app/messages-programmes" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'messages_programmes' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-comment-dots w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'messages_programmes' ? t.iconActive : t.subIcon"></i>Communication
                        </a>
                    </div>
                </template>

                <template v-if="$account.account.can_settings">
                    <p :class="[t.sectionLabel, 'px-3 pb-1.5 pt-5 text-[10px] font-semibold uppercase tracking-[0.12em]']">Configuration</p>
                    <button @click="configurationsOpen = !configurationsOpen"
                        :class="[t.navItemInactive, 'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 border-l-2']">
                        <i :class="[t.icon, 'fas fa-cog w-4 text-center text-xs shrink-0']"></i>
                        <span>Paramètres</span>
                        <i :class="[t.chevron, 'fas fa-chevron-down text-[10px] ml-auto transition-transform duration-200 shrink-0', configurationsOpen ? 'rotate-180' : '']"></i>
                    </button>
                    <div :class="[t.submenuBorder, 'overflow-hidden transition-all duration-200 ml-7 pl-3 border-l']"
                        :style="configurationsOpen ? 'max-height: 400px; opacity: 1' : 'max-height: 0; opacity: 0'">
                        <a href="/app/parametres/abonnement" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'abonnement' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-credit-card w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'abonnement' ? t.iconActive : t.subIcon"></i>Abonnement
                        </a>
                        <a href="/app/parametres/sms" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'parametres-sms' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-message w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'parametres-sms' ? t.iconActive : t.subIcon"></i>SMS
                        </a>
                        <a href="/app/parametres/societe" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'societe' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-building w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'societe' ? t.iconActive : t.subIcon"></i>Société
                        </a>
                        <a href="/app/parametres/tech" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'parametres-tech' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-users-gear w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'parametres-tech' ? t.iconActive : t.subIcon"></i>Utilisateurs
                        </a>
                        <a href="/app/parametres/config-message" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'parametre-message-config' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-envelope-open-text w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'parametre-message-config' ? t.iconActive : t.subIcon"></i>Messages programmés
                        </a>
                        <a href="/app/parametres/metier" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'parametre-metier' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-sliders w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'parametre-metier' ? t.iconActive : t.subIcon"></i>Règles métier
                        </a>
                        <a href="/app/references" class="flex items-center gap-2.5 py-2 px-2 text-sm rounded-md transition-colors duration-150" :class="currentRouteKey == 'references' ? t.subItemActive : t.subItemInactive">
                            <i class="fas fa-barcode w-3.5 text-center text-xs shrink-0" :class="currentRouteKey == 'references' ? t.iconActive : t.subIcon"></i>Références appareils
                        </a>
                    </div>
                </template>

            </nav>

            <!-- User footer -->
            <div :class="[t.footerBorder, 'shrink-0 border-t p-3']">
                <div @click="$router.push({name: 'ParametreAccount'})" :class="[t.footerHover, 'flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors group']">
                    <div class="relative shrink-0">
                        <div :class="[t.avatar, 'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold']">
                            {{ ($account.account.prenom.charAt(0) + $account.account.nom.charAt(0)).toUpperCase() }}
                        </div>
                        <div :class="[t.avatarDot, 'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 rounded-full']"></div>
                    </div>
                    <div class="flex-1 min-w-0">
                        <div :class="[t.userName, 'text-sm font-medium truncate leading-tight']">{{ $account.account.prenom + ' ' + $account.account.nom }}</div>
                        <div :class="[t.userCompany, 'text-[11px] truncate mt-0.5']">{{ $account.account.societe.nom }}</div>
                    </div>
                </div>
            </div>
        </aside>

        <main class="mt-14 md:mt-0 md:pl-[280px] md:pt-14 p-6 min-h-screen transition-all">
            <div class="max-w-[1350px] mx-auto p-6">
                <slot></slot>
            </div>
        </main>

        <modal name="modal-connect-mobile" height="auto" :scrollable="true" :adaptive="true">
            <div class="p-6 min-h-[50vh] max-h-[100vh] overflow-y-auto">
                <div class="flex items-center justify-between mb-4 pb-4 border-b">
                    <h2 class="text-xl font-bold">Connexion à l'application mobile</h2>
                    <button @click="$modal.hide('modal-connect-mobile');"><i class="fas fa-times text-gray-400 hover:text-gray-700 transition-colors"></i></button>
                </div>
                <div class="space-y-6">
                    <div class="text-center">
                        <div class="w-14 h-14 bg-orange-600 rounded-xl flex items-center justify-center mx-auto mb-4"><i class="fas fa-mobile-alt text-white text-xl"></i></div>
                        <h3 class="text-lg font-bold text-gray-900 mb-1">Connectez-vous sur mobile</h3>
                        <p class="text-sm text-gray-500">Accédez à TermoFlow partout, à tout moment</p>
                    </div>
                    <div class="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <h4 class="font-semibold text-gray-900 mb-3 text-sm">Pourquoi utiliser l'app mobile ?</h4>
                        <ul class="space-y-2">
                            <li v-for="item in ['Accès aux interventions en déplacement', 'Prise de photos liées aux interventions', 'Synchronisation automatique en temps réel']" :key="item" class="flex items-center gap-3 text-sm text-gray-600">
                                <div class="w-5 h-5 rounded bg-orange-100 flex items-center justify-center shrink-0"><i class="fas fa-check text-orange-600 text-[10px]"></i></div>
                                {{ item }}
                            </li>
                        </ul>
                    </div>
                    <div>
                        <h4 class="font-semibold text-gray-900 mb-3 text-sm">Comment ça marche ?</h4>
                        <div class="space-y-2.5">
                            <div class="bg-blue-50 border border-blue-200 p-4 rounded-lg flex items-start gap-3.5">
                                <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">1</div>
                                <div><p class="text-sm font-semibold text-gray-900">Ouvrez un navigateur et allez sur :</p><p class="text-base font-bold text-blue-600 mt-0.5">mobile.termoflow.fr</p><p class="text-xs text-gray-500 mt-1">Safari sur iPhone · Chrome sur Android</p></div>
                            </div>
                            <div class="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-start gap-3.5">
                                <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">2</div>
                                <div class="w-full"><p class="text-sm font-semibold text-gray-900 mb-2">Ajoutez l'application à l'écran d'accueil</p>
                                    <div class="space-y-1.5 text-xs text-gray-600">
                                        <div class="bg-white p-2.5 rounded border border-gray-200"><p class="font-semibold mb-0.5">📱 iPhone</p><p>Appuyer sur <strong>"..."</strong> → <strong>"Partager"</strong> → <strong>"Sur l'écran d'accueil"</strong></p></div>
                                        <div class="bg-white p-2.5 rounded border border-gray-200"><p class="font-semibold mb-0.5">🤖 Android</p><p>Menu <strong>⋮</strong> → <strong>"Ajouter à l'écran d'accueil"</strong></p></div>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-green-50 border border-green-200 p-4 rounded-lg flex items-start gap-3.5">
                                <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">3</div>
                                <div><p class="text-sm font-semibold text-gray-900">Ouvrez Termoflow depuis l'icône créée</p><p class="text-xs text-gray-500 mt-1">L'application s'ouvrira en plein écran comme une vraie app.</p></div>
                            </div>
                            <div class="bg-orange-50 border border-orange-200 p-4 rounded-lg flex items-start gap-3.5">
                                <div class="w-7 h-7 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-sm shrink-0">4</div>
                                <div><p class="text-sm font-semibold text-gray-900">Entrez le code reçu par email</p><p class="text-xs text-gray-500 mt-1">Vous serez automatiquement connecté à votre compte.</p></div>
                            </div>
                        </div>
                    </div>
                    <button :disabled="loadingSendCode" @click="sendCode"
                        class="w-full py-3 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                        :class="loadingSendCode ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-500 text-white'">
                        <i v-if="loadingSendCode" class="fas fa-circle-notch fa-spin text-xs"></i>
                        <i v-else class="fas fa-paper-plane text-xs"></i>
                        {{ loadingSendCode ? 'Envoi en cours…' : 'Recevoir le code par email' }}
                    </button>
                </div>
            </div>
        </modal>

        <modal-add-intervention id="modal-add-intervention-template" />
        <assistance />
    </div>
</template>

<script>
import moment from 'moment';
import ModalAddIntervention from '@/views/Intervention/Components/ModalAddIntervention.vue';
import Assistance from '../views/Assistance/Assistance.vue';

// ─────────────────────────────────────────────────────────────────────────────
// THEMES
// Pour ajouter un nouveau thème : ajouter une clé ici. Le template ne change pas.
// Clé utilisée : this.$account.account.theme  ('dark' | 'light' | 'orange' | ...)
// ─────────────────────────────────────────────────────────────────────────────
const THEMES = {

    dark: {
        sidebar:            'bg-[#0D1117] border-r border-[#1E2432]',
        mobileTopbar:       'bg-[#0D1117] border-b border-[#1E2432]',
        mobileMenuBtn:      'text-slate-400 hover:text-white hover:bg-white/10',
        logoBorder:         'border-b border-[#1E2432]',
        logoText:           'text-white',
        logoUrl:            'https://storage.victoranw.fr/file/hGf4I3ecnS',
        ctaBtn:             'bg-orange-600 hover:bg-orange-500 text-white',
        mobileBanner:       'border border-[#252E3F] bg-[#111827]',
        mobileBannerIconBg: 'bg-[#1E2432]',
        mobileBannerIcon:   'text-slate-400',
        mobileBannerTitle:  'text-slate-300',
        mobileBannerSub:    'text-slate-500',
        mobileBannerBtn:    'text-orange-400 hover:text-orange-300',
        sectionLabel:       'text-slate-500',
        navItemActive:      'bg-white/[0.09] text-white font-semibold border-orange-500',
        navItemInactive:    'text-slate-300 hover:text-white hover:bg-white/[0.06] border-transparent',
        icon:               'text-slate-500',
        iconActive:         'text-orange-400',
        chevron:            'text-slate-500',
        submenuBorder:      'border-[#252E3F]',
        subItemActive:      'text-white font-semibold',
        subItemInactive:    'text-slate-400 hover:text-slate-100',
        subIcon:            'text-slate-600',
        badge:              'bg-orange-500/20 text-orange-400',
        footerBorder:       'border-[#1E2432]',
        footerHover:        'hover:bg-white/[0.06]',
        avatar:             'bg-[#1A2035] text-slate-200 ring-1 ring-[#2A3347]',
        avatarDot:          'border-[#0D1117]',
        userName:           'text-slate-200',
        userCompany:        'text-slate-500',
    },

    light: {
        sidebar:            'bg-white border-r border-gray-200',
        mobileTopbar:       'bg-white border-b border-gray-200',
        mobileMenuBtn:      'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
        logoBorder:         'border-b border-gray-200',
        logoText:           'text-gray-900',
        logoUrl:            'https://storage.victoranw.fr/file/hGf4I3ecnS',
        ctaBtn:             'bg-orange-600 hover:bg-orange-500 text-white',
        mobileBanner:       'border border-gray-200 bg-gray-50',
        mobileBannerIconBg: 'bg-gray-200',
        mobileBannerIcon:   'text-gray-500',
        mobileBannerTitle:  'text-gray-700',
        mobileBannerSub:    'text-gray-400',
        mobileBannerBtn:    'text-orange-600 hover:text-orange-700',
        sectionLabel:       'text-gray-400',
        navItemActive:      'bg-orange-50 text-orange-800 font-semibold border-orange-500',
        navItemInactive:    'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-transparent',
        icon:               'text-gray-400',
        iconActive:         'text-orange-500',
        chevron:            'text-gray-400',
        submenuBorder:      'border-gray-200',
        subItemActive:      'text-orange-700 font-semibold',
        subItemInactive:    'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
        subIcon:            'text-gray-400',
        badge:              'bg-orange-100 text-orange-600',
        footerBorder:       'border-gray-200',
        footerHover:        'hover:bg-gray-100',
        avatar:             'bg-gray-200 text-gray-700',
        avatarDot:          'border-white',
        userName:           'text-gray-800',
        userCompany:        'text-gray-400',
    },

    orange: {
        sidebar:            'bg-orange-600 border-r border-orange-700',
        mobileTopbar:       'bg-orange-600 border-b border-orange-700',
        mobileMenuBtn:      'text-orange-100 hover:text-white hover:bg-black/10',
        logoBorder:         'border-b border-orange-700',
        logoText:           'text-white',
        logoUrl:            'https://storage.termoflow.fr/file/VjYPlxfQQV',
        ctaBtn:             'bg-white hover:bg-orange-50 text-orange-700',
        mobileBanner:       'border border-orange-700 bg-orange-700/40',
        mobileBannerIconBg: 'bg-orange-700',
        mobileBannerIcon:   'text-orange-200',
        mobileBannerTitle:  'text-white',
        mobileBannerSub:    'text-orange-200',
        mobileBannerBtn:    'text-white hover:text-orange-100',
        sectionLabel:       'text-orange-200',
        navItemActive:      'bg-black/20 text-white font-semibold border-white',
        navItemInactive:    'text-orange-100 hover:text-white hover:bg-black/10 border-transparent',
        icon:               'text-orange-300',
        iconActive:         'text-white',
        chevron:            'text-orange-300',
        submenuBorder:      'border-orange-500',
        subItemActive:      'text-white font-semibold',
        subItemInactive:    'text-orange-200 hover:text-white hover:bg-black/10',
        subIcon:            'text-orange-400',
        badge:              'bg-black/20 text-white',
        footerBorder:       'border-orange-700',
        footerHover:        'hover:bg-black/10',
        avatar:             'bg-orange-700 text-white ring-1 ring-orange-500',
        avatarDot:          'border-orange-600',
        userName:           'text-white',
        userCompany:        'text-orange-200',
    },

    // ── Ajouter un thème ici ──────────────────────────────────────────────────
    // monTheme: {
    //     sidebar: '...',
    //     ...même liste de clés que ci-dessus...
    // },
};

export default {
    name: "AppTemplate",
    components: { ModalAddIntervention, Assistance },
    data() {
        return {
            sidebarOpen: false,
            configurationsOpen: false,
            gestionOpen: false,
            materielOpen: false,
            documentsOpen: false,
            searchQuery: '',
            searchResults: { clients: [], interventions: [] },
            searchLoading: false,
            searchOpen: false,
            searchTimeout: null,
            pastilles: {},
            loadingSendCode: false,
        }
    },
    filters: {
        momentDate(value) {
            if (!value) return '-';
            return moment(value).format('DD/MM/YYYY');
        }
    },
    computed: {
        // Thème actif — retombe sur 'light' si la valeur est inconnue
        theme() {
            const val = this.$account.account.theme;
            return THEMES[val] ? val : 'light';
        },

        // Raccourci vers le jeu de classes du thème actif
        t() {
            return THEMES[this.theme];
        },

        hasResults() {
            return this.searchResults.clients.length || this.searchResults.interventions.length;
        },

        currentRouteKey() {
            const keyGroup = {
                Home: 'home',
                ClientList: 'clients', ClientView: 'clients',
                InterventionList: 'interventions',
                ChantierList: 'chantiers',
                ParametreList: 'parametres', TechParamList: 'parametres-tech',
                Statistiques: 'statistiques', StatistiquesPremium: 'statistiques',
                FacturationList: 'facturations',
                References: 'references',
                TypeInterventionsSettings: 'type-interventions',
                MessagesProgrammes: 'messages_programmes',
                ParametreSociete: 'societe',
                Stock: 'stock',
                ParametreSms: 'parametres-sms',
                ParametreTravaux: 'parametres-travaux',
                ParametreConfigMessage: 'parametre-message-config',
                Bouteilles: 'bouteilles', Detecteurs: 'detecteurs',
                Contrats: 'contrats',
                AttestationEntretienList: 'attestation_entretien',
                CerfaList: 'cerfa',
                ParametreTarifs: 'tarifs',
                Planning: 'planning',
                ParametreCategorieAppareil: 'parametre-categorie-appareil',
                ParametrePointControle: 'parametre-point-controle',
                ParametreMetier: 'parametre-metier',
                ParametreAbonnement: 'abonnement',
            };
            return keyGroup[this.$route.name] || '';
        },

        warningLicence() {
            if (this.$account.account.societe.date_licence) {
                const diffDays = moment(this.$account.account.societe.date_licence).diff(moment(), 'days');
                if (diffDays <= 30) return `Votre licence expire dans ${diffDays} jour(s)`;
            }
            return '';
        },

        licenceExpired() {
            if (this.$account.account.societe.date_licence) {
                return moment().isAfter(moment(this.$account.account.societe.date_licence));
            }
            return false;
        },
    },
    methods: {
        onSearchInput() {
            clearTimeout(this.searchTimeout);
            if (!this.searchQuery || this.searchQuery.length < 2) {
                this.searchResults.clients = [];
                this.searchResults.interventions = [];
                return;
            }
            this.searchTimeout = setTimeout(this.performSearch, 300);
        },
        performSearch() {
            this.searchLoading = true;
            this.$request.post('global-search', { query: this.searchQuery })
                .then(r => {
                    this.searchResults.clients = r.clients || [];
                    this.searchResults.interventions = r.interventions || [];
                })
                .finally(() => { this.searchLoading = false; });
        },
        goToClient(client) {
            this.searchOpen = false; this.searchQuery = '';
            window.location.href = `/app/clients/${client.id}`;
        },
        goToIntervention(intervention) {
            this.searchOpen = false; this.searchQuery = '';
            window.location.href = `/app/interventions?intervention_id=${intervention.id}`;
        },
        handleClickOutside(e) {
            if (!this.$el.contains(e.target)) this.searchOpen = false;
        },
        async sendCode() {
            this.loadingSendCode = true;
            const response = await this.$request.post('/mobile/send-code', { email: this.$account.account.email });
            this.loadingSendCode = false;
            if (response == 1) {
                this.$toast.success('Code envoyé par email');
                this.$modal.hide('modal-connect-mobile');
                this.$account.account.mobile_token = 'pending';
            } else {
                this.$toast.error('Erreur lors de l\'envoi du code');
            }
        },
        async getPastilles(keyword = null) {
            const response = await this.$request.get('/dashboard/pastilles', { params: { keyword } });
            if (keyword) { this.pastilles[keyword] = response[keyword] || 0; }
            else { this.pastilles = response; }
        },
    },
    mounted() {
        const configRoutes = ['parametres', 'parametres-tech', 'type_interventions', 'societe', 'type-interventions', 'parametres-travaux', 'references', 'parametres-sms', 'tarifs', 'parametre-message-config', 'parametre-categorie-appareil', 'parametre-point-controle', 'parametre-metier', 'abonnement'];
        if (configRoutes.includes(this.currentRouteKey)) this.configurationsOpen = true;
        if (['statistiques', 'messages_programmes'].includes(this.currentRouteKey)) this.gestionOpen = true;
        if (['stock', 'bouteilles', 'detecteurs'].includes(this.currentRouteKey)) this.materielOpen = true;
        if (['contrats', 'attestation_entretien', 'cerfa'].includes(this.currentRouteKey)) this.documentsOpen = true;

        document.addEventListener('click', this.handleClickOutside);
        this.$eventHub.$on('error-toast', (msg) => { this.$toast.error(msg); });
        this.$eventHub.$on('refresh-pastilles', (keyword) => { this.getPastilles(keyword); });
        this.getPastilles();
        if (this.licenceExpired) this.$router.push({ name: 'LicenceExpired' });
        document.title = 'Termoflow - ' + this.$account.account.societe.nom;
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
}
</script>

<style scoped>
aside nav::-webkit-scrollbar       { width: 3px; }
aside nav::-webkit-scrollbar-track { background: transparent; }

aside.theme-dark   nav::-webkit-scrollbar-thumb       { background: #252E3F; border-radius: 4px; }
aside.theme-dark   nav::-webkit-scrollbar-thumb:hover { background: #303A50; }
aside.theme-light  nav::-webkit-scrollbar-thumb       { background: #E5E7EB; border-radius: 4px; }
aside.theme-light  nav::-webkit-scrollbar-thumb:hover { background: #D1D5DB; }
aside.theme-orange nav::-webkit-scrollbar-thumb       { background: rgba(0,0,0,0.2); border-radius: 4px; }
aside.theme-orange nav::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.3); }
</style>